/**
 * Web API Service - Aggregator for all domain APIs
 * Mirrors ipcService.ts structure for seamless replacement
 */

import * as authApi from './authApi'
import * as poolApi from './poolApi'
import * as accountApi from './accountApi'
import * as systemApi from './systemApi'
import * as controlApi from './controlApi'
import * as monitoringApi from './monitoringApi'
import * as touApi from './touApi'
import * as permissionsApi from './permissionsApi'
import * as evcsApi from './evcsApi'
import type { ApiResponse } from '../types'

/**
 * Global unauthorized handler - called when a 401 error is detected
 * This is set by App.tsx on mount
 */
let unauthorizedHandler: (() => void) | null = null
let lastUnauthorizedTime = 0
const UNAUTHORIZED_THROTTLE_MS = 5000 // Only trigger once per 5 seconds

export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler
}

/**
 * Check if a response indicates an unauthorized error (401)
 * If so, trigger the global unauthorized handler (rate-limited)
 */
function checkUnauthorized(response: ApiResponse): void {
  if (!response.success && response.errorCode === 'UNAUTHORIZED') {
    const now = Date.now()

    // Throttle unauthorized handler to prevent hundreds of logout attempts
    if (now - lastUnauthorizedTime < UNAUTHORIZED_THROTTLE_MS) {
      console.warn('[WebAPI] Unauthorized error detected but throttled (already handled recently)')
      return
    }

    lastUnauthorizedTime = now
    console.warn('[WebAPI] Unauthorized error detected - triggering logout')

    if (unauthorizedHandler) {
      unauthorizedHandler()
    }
  }
}

/**
 * Wrapper to check unauthorized for all API calls
 */
async function withUnauthorizedCheck<T>(
  apiCall: () => Promise<ApiResponse<T>>
): Promise<ApiResponse<T>> {
  const response = await apiCall()
  checkUnauthorized(response)
  return response
}

export const webApiService = {
  // Auth
  auth: {
    login: async (apiKey: string, type: 'bearer' | 'token' = 'bearer'): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => authApi.login(apiKey, type))
    },
    logout: async (): Promise<ApiResponse> => {
      return authApi.logout()
    },
    check: async (): Promise<ApiResponse> => {
      return authApi.check()
    },
    setEnvironment: async (env: 'staging' | 'production'): Promise<ApiResponse> => {
      return authApi.setEnvironment(env)
    },
    getEnvironment: async (): Promise<ApiResponse> => {
      return authApi.getEnvironment()
    },
  },

  // Accounts
  accounts: {
    getAll: async (page: number, perPage?: number): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => accountApi.getAll(page, perPage))
    },
    getById: async (accountID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => accountApi.getById(accountID))
    },
  },

  // Pools
  pools: {
    getAll: async (): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => poolApi.getAll())
    },
    getById: async (poolID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => poolApi.getById(poolID))
    },
    create: async (data: any): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => poolApi.create(data))
    },
    update: async (poolID: string, data: any): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => poolApi.update(poolID, data))
    },
    delete: async (poolID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => poolApi.deletePool(poolID))
    },
    getHistory: async (poolID: string, timestamp: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => poolApi.getHistory(poolID, timestamp))
    },
  },

  // Systems
  systems: {
    get: async (systemID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => systemApi.get(systemID))
    },
    getByPool: async (poolID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => systemApi.getByPool(poolID))
    },
    getByPoolPaginated: async (
      poolID: string,
      opts: { limit: number; offset: number }
    ): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => systemApi.getByPoolPaginated(poolID, opts))
    },
    getSystemIds: async (poolID: string, listAll?: boolean): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => systemApi.getSystemIds(poolID, listAll))
    },
    add: async (poolID: string, systemID: string, activeFrom?: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => systemApi.add(poolID, systemID, activeFrom))
    },
    remove: async (poolID: string, systemID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => systemApi.remove(poolID, systemID))
    },
    updateSystem: async (
      poolID: string,
      systemID: string,
      data: { activeFrom?: string; activeUntil?: string | null }
    ): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => systemApi.updateSystem(poolID, systemID, data))
    },
    getPoolsBySystem: async (systemID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => systemApi.getPoolsBySystem(systemID))
    },
    getHistory: async (systemID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => systemApi.getHistory(systemID))
    },
  },

  // Control
  control: {
    submit: async (
      poolID: string,
      tasks: Array<{ powerTarget: number; startTime: string; endTime: string }>,
      options?: { unit?: 'W' | 'kW' | 'MW'; idempodencyKey?: string }
    ): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => controlApi.submit(poolID, tasks, options))
    },
    getCurrent: async (poolID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => controlApi.getCurrent(poolID))
    },
    deleteAll: async (poolID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => controlApi.deleteAll(poolID))
    },
    deleteSignal: async (poolID: string, taskID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => controlApi.deleteSignal(poolID, taskID))
    },
  },

  // Time of Use
  tou: {
    getSettings: async (systemID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => touApi.getSettings(systemID))
    },
    enable: async (systemID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => touApi.enable(systemID))
    },
    disable: async (systemID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => touApi.disable(systemID))
    },
  },

  // Permissions
  permissions: {
    getPermissions: async (): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => permissionsApi.getPermissions())
    },
  },

  // EVCS Management
  evcs: {
    getSystemWithGateways: async (systemID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => evcsApi.getSystemWithGateways(systemID))
    },
    getEVConfiguration: async (gatewayID: string, applianceID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => evcsApi.getEVConfiguration(gatewayID, applianceID))
    },
    listEVProfiles: async (systemID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => evcsApi.listEVProfiles(systemID))
    },
    getSystemLiveMeasurements: async (systemID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => evcsApi.getSystemLiveMeasurements(systemID))
    },
  },

  // Monitoring
  monitoring: {
    getPoolState: async (poolID: string): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => monitoringApi.getPoolState(poolID))
    },
    getFlexBands: async (poolID: string, forecastDuration?: number): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => monitoringApi.getFlexBands(poolID, forecastDuration))
    },
    getChargingSessions: async (poolID: string, params?: any): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => monitoringApi.getChargingSessions(poolID, params))
    },
    getPIDState: (poolID: string) => withUnauthorizedCheck(() => monitoringApi.getPIDState(poolID)),
    getSystemChargingSessions: async (
      systemID: string,
      params: { start: string; end: string }
    ): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() =>
        monitoringApi.getSystemChargingSessions(systemID, params)
      )
    },
    getPoolGCPForecasts: async (
      poolID: string,
      purposes?: monitoringApi.GCPForecastPurpose[]
    ): Promise<ApiResponse> => {
      return withUnauthorizedCheck(() => monitoringApi.getPoolGCPForecasts(poolID, purposes))
    },
  },
}
